import { Injectable, inject } from '@angular/core';
import { SkyApiService } from '@skyeng/libs/shared/api';
import { vimCoreSentry } from '@skyeng/libs/vimbox-core/sentry';
import { VimCoreAuthService } from '@skyeng/libs/vimbox-core/user';
import { type Observable, type Subscription, catchError, forkJoin, map, of } from 'rxjs';

import {
  type ContentIssueApiProvider,
  type ErrorReport,
  type ErrorReportSendOptions,
  type LegacyEndpointConfig,
  type MyReportDetail,
  type MyReportList,
  type MyUnreadReports,
  type ProductKey,
  type SuccessResponse,
} from './interfaces';
import {
  ERROR_REPORT_APP_VERSION,
  ERROR_REPORT_DUAL_WRITE_DISABLED,
  ERROR_REPORT_LEGACY_CONFIG,
  ERROR_REPORT_LEGACY_CONFIG_BY_PRODUCT,
  ERROR_REPORT_PRODUCT_KEY,
  ERROR_REPORT_PROXY_ENDPOINT,
  ERROR_REPORT_UPLOAD_PROVIDER,
} from './tokens';

const E2E_MODE_LOCAL_STORAGE_KEY = 'isE2EMode';

const DUAL_WRITE_FAILED_RESULT = '__failed__';
type DualWriteResult = unknown | typeof DUAL_WRITE_FAILED_RESULT;

interface ProxyErrorReportPayload {
  product: string;
  // Originating surface — proxy stores it alongside `product`. Curator submits
  // set `source = 'curator-cabinet'` while `product` carries the dropdown pick;
  // every other surface leaves it equal to `product`.
  source: string;
  message: string;
  roomHash: string | null;
  stepUuid: string | null;
  category: string | null;
  resourceUrls: string[];
  // Original client filenames aligned by index with `resourceUrls`. The S3 key
  // only keeps an ASCII slug, so the human-readable name is sent here for display.
  resourceNames: string[];
  clientMeta: Record<string, unknown>;
  // Math-trainer surface populates this with task identifiers (`task_id`,
  // `submission_id`, `task_student_id`, `code_length`, `last_verdict`). The
  // proxy stores it verbatim under `context.mathContext`. Omitted for
  // non-math submits so the renderer's "context absent" branch fires for
  // them naturally.
  mathContext?: Record<string, unknown>;
}

/**
 * Product-agnostic bug-report API service.
 *
 * Behaviour is configured by DI tokens declared in `./tokens.ts`:
 *   * `ERROR_REPORT_PRODUCT_KEY`   — value sent in the proxy `product` field
 *   * `ERROR_REPORT_PROXY_ENDPOINT` — host(s) for the new proxy backend
 *   * `ERROR_REPORT_LEGACY_CONFIG`  — optional legacy backend (dual-write); `null` = proxy-only
 *   * `ERROR_REPORT_UPLOAD_PROVIDER`— per-product upload bridge
 *   * `ERROR_REPORT_DUAL_WRITE_DISABLED` — optional kill switch that falls back to legacy-only
 *
 * AI Tutor binds the legacy config + kill switch (preserving SINGULAR-202's
 * dual-write strangler). Math Trainer / Student Cabinet / Curator Cabinet bind
 * `legacy = null`, so for them the same service degrades to a clean proxy-only
 * POST.
 */
@Injectable()
export class ErrorReportApiService implements ContentIssueApiProvider {
  // Default fallback `'ai-tutor'` preserves SINGULAR-202 behaviour for the AI
  // Tutor binding even if a consumer omits the explicit token. Avatar (math
  // + cabinet) and curator-cabinet both pass `product` per-call, so the
  // default never reaches the wire — see `ErrorReportSendOptions`.
  private product = inject(ERROR_REPORT_PRODUCT_KEY, { optional: true }) ?? 'ai-tutor';
  private proxyEndpoint = inject(ERROR_REPORT_PROXY_ENDPOINT);
  private legacyConfig = inject(ERROR_REPORT_LEGACY_CONFIG, { optional: true });
  private legacyConfigByProduct = inject(ERROR_REPORT_LEGACY_CONFIG_BY_PRODUCT, { optional: true });
  private uploadProvider = inject(ERROR_REPORT_UPLOAD_PROVIDER);
  private dualWriteDisabledCheck = inject(ERROR_REPORT_DUAL_WRITE_DISABLED, { optional: true });
  private appVersion = inject(ERROR_REPORT_APP_VERSION, { optional: true });

  private skyApiService = inject(SkyApiService);
  private vimCoreAuthService = inject(VimCoreAuthService);

  send(report: ErrorReport, options?: ErrorReportSendOptions): Observable<SuccessResponse> {
    const proxy$ = this.postProxy(this.buildProxyPayload(report, options));

    // Per-product map (preferred) wins over the single static binding. Lets
    // the curator selector pick a product → matching legacy endpoint at send
    // time without re-scoping the service. Falls back to the static config
    // for surfaces that still bind one product per service instance.
    const resolvedLegacy = this.resolveLegacyConfig(options);

    if (!resolvedLegacy) {
      return proxy$.pipe(map(() => ({ success: true })));
    }

    const legacy$ = this.postLegacy(report, resolvedLegacy, options);

    if (this.dualWriteDisabledCheck?.() === true) {
      return legacy$.pipe(map(() => ({ success: true })));
    }

    return forkJoin({
      legacy: legacy$.pipe(catchError(error => of<DualWriteResult>(this.captureFailure(error)))),
      proxy: proxy$.pipe(catchError(error => of<DualWriteResult>(this.captureFailure(error)))),
    }).pipe(
      map(({ legacy, proxy }) => {
        const legacyFailed = legacy === DUAL_WRITE_FAILED_RESULT;
        const proxyFailed = proxy === DUAL_WRITE_FAILED_RESULT;

        if (legacyFailed && proxyFailed) {
          throw new Error('bug_report_dual_write: both endpoints failed');
        }

        if (legacyFailed || proxyFailed) {
          vimCoreSentry.captureException(
            new Error(`bug_report_dual_write_partial: legacyFailed=${legacyFailed} proxyFailed=${proxyFailed}`),
          );
        }

        return { success: true };
      }),
    );
  }

  /**
   * Create a report and resolve its proxy id. Same proxy POST as {@link send},
   * but surfaces the created report's `id` (the proxy already returns
   * `{ id, notifyStatus }`; `send` discards it). The conversational "new
   * appeal" flow in the cabinet needs the id to open the live chat right after
   * the first message.
   *
   * The proxy write is REQUIRED — its `id` is what the chat thread binds to —
   * so a proxy failure propagates as an error. The legacy dual-write (when a
   * surface configures one) stays best-effort: a legacy failure is captured to
   * Sentry but never blocks the chat-backed proxy report. The dual-write
   * kill-switch is intentionally NOT honoured here: it falls back to
   * legacy-only, which yields no proxy id and therefore no chat — not a valid
   * state for this flow. Only proxy-backed surfaces (cabinet) call this.
   */
  createReport(report: ErrorReport, options?: ErrorReportSendOptions): Observable<{ reportId: number }> {
    const proxy$ = this.postProxy(this.buildProxyPayload(report, options));
    const resolvedLegacy = this.resolveLegacyConfig(options);

    if (!resolvedLegacy) {
      return proxy$.pipe(map(body => ({ reportId: this.extractReportId(body) })));
    }

    const legacy$ = this.postLegacy(report, resolvedLegacy, options).pipe(
      catchError(error => of<DualWriteResult>(this.captureFailure(error))),
    );

    return forkJoin({ legacy: legacy$, proxy: proxy$ }).pipe(
      map(({ proxy }) => ({ reportId: this.extractReportId(proxy) })),
    );
  }

  uploadFile(file: File | null, uploadInitiator?: string): Observable<{ id: number; file: string } | null> {
    if (!file) {
      return of(null);
    }

    return this.uploadProvider.uploadFile(file, uploadInitiator).pipe(map(url => ({ id: 0, file: url })));
  }

  /**
   * Fetch the caller's own report history. Proxy-only (no legacy dual-write
   * — the legacy `ai-tutor-api` had no equivalent endpoint).
   *
   * The proxy enforces `WHERE user_id = jwt.userId` server-side; the client
   * cannot pass a `userId` filter even if it wanted to.
   */
  listMine(opts?: { cursor?: number; limit?: number }): Observable<MyReportList> {
    const params: Record<string, string | number> = {
      ...(opts?.cursor !== undefined ? { cursor: opts.cursor } : {}),
      ...(opts?.limit !== undefined ? { limit: opts.limit } : {}),
    };
    return this.skyApiService.get<MyReportList>(this.proxyEndpoint, '/api/v1/error-report/my', {
      params,
      withCredentials: true,
      token: this.vimCoreAuthService.getToken(),
    });
  }

  /**
   * Fetch one of the caller's own reports by id. Server returns 404 when the
   * row exists but belongs to another user (ownership-by-construction inside
   * the proxy repository) so this method MUST surface 404 as "not found",
   * never as "forbidden".
   */
  getMine(id: number): Observable<MyReportDetail> {
    return this.skyApiService.get<MyReportDetail>(this.proxyEndpoint, `/api/v1/error-report/my/${id}`, {
      withCredentials: true,
      token: this.vimCoreAuthService.getToken(),
    });
  }

  /** Report ids with an unread staff reply — drives the dot on the history list. */
  myUnread(): Observable<MyUnreadReports> {
    return this.skyApiService.get<MyUnreadReports>(this.proxyEndpoint, '/api/v1/error-report/messages/my-unread', {
      withCredentials: true,
      token: this.vimCoreAuthService.getToken(),
    });
  }

  onUploading(cb: (uploadingEvent: unknown) => unknown): Subscription {
    return this.uploadProvider.onUploading(cb);
  }

  private postLegacy(
    report: ErrorReport,
    legacy: LegacyEndpointConfig,
    options?: ErrorReportSendOptions,
  ): Observable<unknown> {
    // When `mapPayload` is omitted the lib sends the raw `ErrorReport` body —
    // preserves SINGULAR-202 AI Tutor behavior byte-for-byte.
    const body = legacy.mapPayload ? legacy.mapPayload(report, options) : report;

    // `null` ⇒ surface opted out of the legacy write (e.g., avatar's shared
    // service routing math vs. cabinet). Treated as success so the proxy
    // half still resolves via `forkJoin`.
    if (body === null) {
      return of({ skipped: true });
    }

    const httpMethod = legacy.httpMethod ?? 'POST';
    const requestOptions = { params: body, withCredentials: true };

    if (httpMethod === 'PUT') {
      return this.skyApiService.put(legacy.endpoints, legacy.path, requestOptions);
    }

    return this.skyApiService.post(legacy.endpoints, legacy.path, requestOptions);
  }

  private postProxy(payload: ProxyErrorReportPayload): Observable<unknown> {
    // The proxy backend reads JWT from the `Authorization: Bearer` header (default
    // `authjwt_token_location = {'headers'}`). Mirror the pattern used by
    // `test-lesson-room.ts::create()` which talks to the same backend.
    return this.skyApiService.post(this.proxyEndpoint, '/api/v1/error-report', {
      params: payload,
      withCredentials: true,
      token: this.vimCoreAuthService.getToken(),
    });
  }

  /** Pull the numeric report id out of the proxy create response (`{ id, notifyStatus }`). */
  private extractReportId(body: unknown): number {
    const id = (body as { id?: unknown } | null)?.id;
    if (typeof id !== 'number' || !Number.isFinite(id)) {
      throw new Error('error_report_create: proxy response missing numeric id');
    }
    return id;
  }

  private buildProxyPayload(report: ErrorReport, options?: ErrorReportSendOptions): ProxyErrorReportPayload {
    const product = options?.product ?? this.product;
    // Forward math context only when the caller actually populated it; this
    // keeps the wire payload unchanged for AI Tutor / curator / cabinet
    // surfaces and lets the proxy's "absent" branch fire naturally for them.
    const mathContext =
      report.mathContext && Object.keys(report.mathContext).length > 0 ? report.mathContext : undefined;
    return {
      product,
      // Default source to product when caller omits it — preserves
      // SINGULAR-364 behaviour for every non-curator surface (proxy stores
      // source = product). Curator passes source explicitly via options.
      source: options?.source ?? product,
      message: report.message,
      roomHash: report.roomHash || null,
      stepUuid: report.stepUuid || null,
      category: report.category || null,
      resourceUrls: report.resourceUrls ?? [],
      resourceNames: report.resourceNames ?? [],
      clientMeta: {
        ...report.clientMeta,
        isE2EMode: this.readIsE2EMode(),
        contentLessonId: report.contentLessonId ?? null,
        stepIndex: report.stepIndex ?? null,
        ...this.readClientContext(),
      },
      ...(mathContext ? { mathContext } : {}),
    };
  }

  private resolveLegacyConfig(options?: ErrorReportSendOptions): LegacyEndpointConfig | null {
    const product = options?.product ?? this.product;
    const fromMap = this.legacyConfigByProduct?.[product as ProductKey];
    return fromMap ?? this.legacyConfig ?? null;
  }

  private readIsE2EMode(): boolean {
    try {
      return globalThis.localStorage?.getItem(E2E_MODE_LOCAL_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  /**
   * Phase B (SINGULAR-202) — capture browser-level context behind the
   * Jira-derived triage fields. Best-effort: omit any field whose runtime
   * surface is unavailable (SSR, jest jsdom restrictions, locked iframe)
   * rather than crashing the submit. Heavier signals (`wsConnected`,
   * `lastConsoleError`, `audioState`) need integration with vim-core /
   * ai-tutor session services and land in a separate follow-up.
   */
  private readClientContext(): Record<string, unknown> {
    const navigatorMeta = this.readNavigatorMeta();
    const windowMeta = this.readWindowMeta();

    return {
      incidentTs: Date.now(),
      ...navigatorMeta,
      ...windowMeta,
      ...(this.appVersion ? { appVersion: this.appVersion } : {}),
    };
  }

  private readNavigatorMeta(): Record<string, unknown> {
    try {
      const navigator = globalThis.navigator;
      return {
        ...(navigator?.userAgent ? { userAgent: navigator.userAgent } : {}),
        ...(navigator?.language ? { locale: navigator.language } : {}),
      };
    } catch {
      return {};
    }
  }

  private readWindowMeta(): Record<string, unknown> {
    try {
      const win = globalThis.window;
      const viewport =
        win?.innerWidth && win.innerHeight ? { viewport: { w: win.innerWidth, h: win.innerHeight } } : {};
      const pathname = win?.location?.pathname;
      return {
        ...viewport,
        ...(pathname ? { urlPathname: pathname } : {}),
      };
    } catch {
      return {};
    }
  }

  private captureFailure(error: unknown): typeof DUAL_WRITE_FAILED_RESULT {
    vimCoreSentry.captureException(error);
    return DUAL_WRITE_FAILED_RESULT;
  }
}
